import React, {useCallback, useEffect, useState} from 'react';
import {
    DataGrid, 
    GridActionsCellItem,
    GridRowEditStopReasons,
    GridRowModes,
    GridToolbarContainer
} from '@mui/x-data-grid';
import {
    Alert,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText, DialogActions, Paper
} from '@mui/material';
import axiosInstance from '../AxiosInstance';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import Box from "@mui/material/Box";
import DeleteIcon from "@mui/icons-material/Delete";
import Snackbar from '@mui/material/Snackbar';
import {
    isGreaterThanZero,
    validateString
} from "../Utilities/validation";
import Draggable from "react-draggable";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from '@mui/icons-material/Warning';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import { useColumnWidths } from '../Utilities/columnWidthUtils';

const defaultColumnWidths = {
    name: 200
};

function PaperComponent(props) {
    return (
        <Draggable handle="#draggable-dialog-title" cancel={'[class*="MuiDialogContent-root"]'}>
            <Paper {...props} />
        </Draggable>
    );
}

function EditToolbar(props) {
    const {
        customers,
        setCustomers,
        setRowModesModel,
        selectedCustomer,
        setSnackbar,
        handleOpenConfirmationDialog
    } = props;

    const handleClick = () => {
        const hasUnsavedRow = customers.some(row => row.isNew === true);
        if (hasUnsavedRow) {
            setSnackbar({
                children: "Please save or cancel the existing new customer first.",
                severity: "warning"
            });
            return;
        }

        const customerId = -1;
        const newRow = {
            customerId,
            name: '',
            channelId: 1,
            externalCustomerId: null,
            integrationProviderName: null,
            isNew: true,
            canDelete: true
        };

        setCustomers((oldRows) => [newRow, ...oldRows]);

        setRowModesModel((oldModel) => ({
            [customerId]: {mode: GridRowModes.Edit, fieldToFocus: 'name'},
            ...oldModel,
        }));

        setTimeout(() => {
            try {
                const nameInput = document.querySelector(`[data-id="${customerId}"] [data-field="name"] input`);
                if (nameInput) {
                    nameInput.focus();
                }
            } catch (error) {
                console.log('Focus failed:', error);
            }
        }, 200);
    };

    const handleDeleteClick = () => {
        if (!selectedCustomer || !selectedCustomer.customerId) {
            console.error("No customer selected for deletion.");
            setSnackbar({children: "No customer selected", severity: "error"});
            return;
        }
        handleOpenConfirmationDialog();
    };

    return (
        <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
            <Tooltip title="Create">
                <IconButton aria-label="create" onClick={handleClick}>
                    <AddIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
                <IconButton aria-label="delete" onClick={handleDeleteClick}>
                    <DeleteIcon />
                </IconButton>
            </Tooltip>
        </GridToolbarContainer>
    );
}

const CustomerTable = () => {

    const [customers, setCustomers] = useState([]);
    const [channels, setChannels] = useState([]);
    const [externalCustomers, setExternalCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [rowModesModel, setRowModesModel] = useState({});
    const [snackbar, setSnackbar] = useState(null);
    const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
    const [hasIntegrationProviders, setHasIntegrationProviders] = useState(false);
    const [loading, setLoading] = useState(true);
    const [loadingExternalCustomers, setLoadingExternalCustomers] = useState(false);
    const { columnWidths, handleColumnResize } = useColumnWidths('customer', defaultColumnWidths);

    const getIntegrationProviderName = (externalCustomerId) => {
        if (!hasIntegrationProviders || !externalCustomerId) {
            return null;
        }
        
        const selectedExternal = externalCustomers.find(
            ext => ext.externalCustomerId === externalCustomerId
        );
        
        return selectedExternal ? selectedExternal.provider : null;
    };

    const handleServerError = (error, customerId, isUpdate = true) => {
        console.error(`Error ${isUpdate ? 'updating' : 'creating'} the customer:`, error);

        if (error.response?.status === 500) {
            const originalRow = customers.find(row => row.customerId === customerId) || {
                customerId,
                externalCustomerId: null,
                name: '',
                channelId: 1
            };
            
            setSnackbar({
                children: `There was an error ${isUpdate ? 'updating' : 'creating'} this customer, please try again.`,
                severity: 'error'
            });
            
            return originalRow;
        }
        
        let errorMessage = error.response?.data || `Error ${isUpdate ? 'updating' : 'creating'} customer`;
        throw new Error(errorMessage);
    };

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            await Promise.all([
                fetchChannels(),
                fetchCustomers(),
                checkIntegrationProviders()
            ]);
            setLoading(false);
        };
        
        fetchInitialData();
    }, []);

    const fetchChannels = async () => {
        try {
            const response = await axiosInstance.get('/channels');
            setChannels(response.data);
        } catch (error) {
            console.error('Error fetching channels:', error);
        }
    };

    const fetchCustomers = async () => {
        try {
            const response = await axiosInstance.get('/customers');
            
            if (!Array.isArray(response.data)) {
                console.error('Expected an array of customers but received:', response.data);
                setSnackbar({
                    children: 'Error loading customers: Invalid data format',
                    severity: 'error'
                });
                return;
            }
            
            const validCustomers = response.data
                .filter(customer => 
                    customer && 
                    typeof customer === 'object' && 
                    'customerId' in customer
                )
                .map(customer => ({
                    ...customer
                }));
                
            setCustomers(validCustomers);
            
            if (validCustomers.length !== response.data.length) {
                console.warn(`Filtered out ${response.data.length - validCustomers.length} invalid customer records`);
            }
        } catch (error) {
            console.error('Error fetching customers:', error);
            setSnackbar({
                children: 'Error loading customers',
                severity: 'error'
            });
        }
    };

    const fetchExternalCustomers = async () => {
        if (!hasIntegrationProviders) {
            setExternalCustomers([]);
            return;
        }
        
        setLoadingExternalCustomers(true);
        try {
            const response = await axiosInstance.get('/customers/externalunlinked');
            setExternalCustomers(response.data);
        } catch (error) {
            console.error('Error fetching external customers:', error);
        } finally {
            setLoadingExternalCustomers(false);
        }
    };

    // todo : probably need a dedicated endpoint for this
    const checkIntegrationProviders = async () => {
        try {
            const response = await axiosInstance.get('/settings');
            setHasIntegrationProviders(
                response.status === 200 && response.data?.isEnabled === true
            );
        } catch (error) {
            if (error.response && error.response.status === 404) {
                setHasIntegrationProviders(false);
            } else {
                console.error('Error checking integration providers:', error);
                setHasIntegrationProviders(false);
            }
        }
    };

    const handleCloseSnackbar = () => setSnackbar(null);

    const handleRowModesModelChange = (newRowModesModel) => {
        setRowModesModel(newRowModesModel);
    };

    const handleRowEditStop = (params, event) => {
        if (params.reason === GridRowEditStopReasons.rowFocusOut) {
            event.defaultMuiPrevented = true;
        }

        if (params.reason === GridRowEditStopReasons.escapeKeyDown) {
            const id = params.id;
            const editedRow = customers.find((row) => row.customerId === id);
            if (editedRow && editedRow.customerId === -1) {
                setCustomers(customers.filter((row) => row.customerId !== -1));
            }
        }
    };

    const handleProcessRowUpdateError = useCallback((error) => {
        setSnackbar({children: error.message, severity: 'error'});
    }, []);

    const handlePromotionRowClick = (params) => {
        if (params.row.isNew) {
            setSelectedCustomer(null);
            return;
        }

        setSelectedCustomer(params.row);
    };

    const handleEditClick = (id) => () => {
        if (hasIntegrationProviders) {
            fetchExternalCustomers();
        }
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.Edit}});
    };

    const handleSaveClick = (id) => () => {
        setRowModesModel({...rowModesModel, [id]: {mode: GridRowModes.View}});
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel({
            ...rowModesModel,
            [id]: {mode: GridRowModes.View, ignoreModifications: true},
        });

        const editedRow = customers.find((row) => row.customerId === id);
        if (editedRow.isNew) {
            setCustomers(customers.filter((row) => row.customerId !== id));
        }
    };

    const handleOpenConfirmationDialog = (type) => {
        setOpenConfirmationDialog(true);
    };

    const handleCloseConfirmationDialog = () => {
        setOpenConfirmationDialog(false);
    };

    const handleConfirmDeleteCustomer = async () => {
        if (!selectedCustomer) {
            console.error("Attempt to delete without selecting a customer.");
            setSnackbar({children: "No customer selected for deletion", severity: "error"});
            return;
        }

        setLoading(true);
        try {
            const response = await axiosInstance.delete(`/customers/${selectedCustomer.customerId}`);
            if (response.status === 204 || response.status === 200) {
                setCustomers((currentCustomers) => currentCustomers.filter(row => row.customerId !== selectedCustomer.customerId));
                setSnackbar({children: "Customer successfully deleted", severity: "success"});
            }
        } catch (error) {
            console.error(`Error deleting the customer: ${error}`);
            setSnackbar({children: `Error deleting the customer: ${error.response?.data || error.message}`, severity: "error"});
        } finally {
            setSelectedCustomer(null);
            handleCloseConfirmationDialog();
            setLoading(false);
        }
    };


    const processRowUpdate = async (newRow) => {
        setLoading(true);
        
        try {
            const updatedRow = {
                ...newRow,
                isNew: false
            };

            if (!validateString(newRow.name)) {
                throw new Error('Please enter a valid value for customer name');
            }

            if (!isGreaterThanZero(newRow.channelId)) {
                throw new Error('Please select a valid channel');
            }

            if (isGreaterThanZero(newRow.customerId)) {
                try {
                    const customerToUpdate = {
                        name: newRow.name,
                        channelId: newRow.channelId,
                        externalCustomerId: hasIntegrationProviders ? (newRow.externalCustomerId || null) : null
                    };
                    
                    await axiosInstance.put(`/customers/${newRow.customerId}`, customerToUpdate);
                    
                    const integrationProviderName = newRow.integrationProviderName || 
                                                   getIntegrationProviderName(newRow.externalCustomerId);
                    
                    const finalUpdatedRow = {
                        ...updatedRow,
                        integrationProviderName
                    };
                    
                    setCustomers(prevCustomers => 
                        prevCustomers.map(row => 
                            (row.customerId === newRow.customerId ? finalUpdatedRow : row)
                        )
                    );
                    
                    setSnackbar({children: 'Customer successfully updated', severity: 'success'});
                    
                    if (window.refreshNotifications)
                        window.refreshNotifications();
                    
                    return finalUpdatedRow;
                } catch (error) {
                    return handleServerError(error, newRow.customerId, true);
                }
            } else {
                try {
                    const customerToCreate = {
                        name: newRow.name,
                        channelId: newRow.channelId,
                        externalCustomerId: hasIntegrationProviders ? (newRow.externalCustomerId || null) : null
                    };
                    
                    const response = await axiosInstance.post(`/customers`, customerToCreate);
                    
                    const integrationProviderName = getIntegrationProviderName(newRow.externalCustomerId);
                    
                    const newCustomer = response.status === 204 ? 
                        { 
                            ...customerToCreate, 
                            customerId: Date.now(),
                            integrationProviderName 
                        } : 
                        { ...response.data, integrationProviderName };
                    
                    setCustomers(prevCustomers =>
                        prevCustomers.map(row =>
                            row.customerId === newRow.customerId ? newCustomer : row
                        )
                    );

                    setSnackbar({children: 'Customer successfully created', severity: 'success'});
                    
                    if (window.refreshNotifications)
                        window.refreshNotifications();
                    
                    return newCustomer;
                } catch (error) {
                    return handleServerError(error, newRow.customerId, false);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    const renderEditNameCell = (params) => {
        return (
            <TextField
                value={params.value}
                onChange={(event) => {
                    const value = event.target.value;
                    if (value.length <= 100) {
                        params.api.setEditCellValue({ id: params.id, field: params.field, value }, event);
                    }
                }}
                slotProps={{ htmlInput: { maxLength: 100 } }}
                fullWidth
            />
        );
    };

    const renderIntegrationCell = (params) => {
        const isInEditMode = rowModesModel[params.id]?.mode === GridRowModes.Edit;
        if (isInEditMode)
            return null;
        
        if (params.row.integrationProviderName)
            return params.row.integrationProviderName;
        
        return (
            <Tooltip title="Not linked">
                <WarningIcon color="warning" />
            </Tooltip>
        );
    };

    const renderEditIntegrationCell = (params) => {
        if (params.row.integrationProviderName) {
            return (
                <Box sx={{ 
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    fontWeight: 'normal',
                    padding: '0 10px'
                }}>
                    {params.row.integrationProviderName}
                </Box>
            );
        }
        
        if (loadingExternalCustomers) {
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', padding: '0 10px' }}>
                    <CircularProgress size={20} />
                </Box>
            );
        }

        return (
            <FormControl fullWidth>
                <Select
                    value={params.row.externalCustomerId || ''}
                    onChange={(event) => {
                        const newValue = event.target.value;
                        
                        params.api.setEditCellValue({ 
                            id: params.id, 
                            field: 'externalCustomerId', 
                            value: newValue 
                        });
                        
                        setTimeout(() => {
                            params.api.stopCellEditMode({ id: params.id, field: 'externalCustomerId' });
                        }, 0);
                    }}
                    MenuProps={{
                        disableScrollLock: true,
                        anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                        },
                        transformOrigin: {
                            vertical: 'top',
                            horizontal: 'left',
                        }
                    }}
                    displayEmpty
                >
                    <MenuItem value="">None</MenuItem>
                    {externalCustomers.map((extCustomer) => (
                        <MenuItem 
                            key={extCustomer.externalCustomerId} 
                            value={extCustomer.externalCustomerId}
                        >
                            {extCustomer.name} ({extCustomer.provider})
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    };

    const baseColumns = [
        {
            field: 'name',
            headerName: 'Name',
            width: columnWidths.name,
            editable: true,
            renderEditCell: renderEditNameCell,
        },
        {
            field: 'channelId',
            headerName: 'Channel',
            width: 120,
            editable: true,
            type: 'singleSelect',
            valueOptions: channels.map((channel) => ({
                value: channel.channelId,
                label: channel.name
            })),
            valueFormatter: (value) => {
                const channel = channels.find(f => f.channelId === value);
                return channel ? channel.name : '';
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({id}) => {
                const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon/>}
                            label="Save"
                            sx={{
                                color: 'primary.main',
                            }}
                            onClick={handleSaveClick(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon/>}
                            label="Cancel"
                            className="textPrimary"
                            onClick={handleCancelClick(id)}
                            color="inherit"
                        />,
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon/>}
                        label="Edit"
                        className="textPrimary"
                        onClick={handleEditClick(id)}
                        color="inherit"
                    />
                ];
            },
        },
    ];
    
    const integrationColumn = {
        field: 'externalCustomerId',
        headerName: 'Integration',
        description: 'Customers can be linked to 3rd party integrations. Once linked, the integration cannot be changed.',
        width: 150,
        editable: (params) => !params.row.integrationProviderName,
        renderCell: renderIntegrationCell,
        renderEditCell: renderEditIntegrationCell,
        cellClassName: (params) => {
            return params.row.integrationProviderName ? 'non-editable' : '';
        },
    };
    
    const columns = hasIntegrationProviders ? 
        [...baseColumns.slice(0, 2), integrationColumn, ...baseColumns.slice(2)] : 
        baseColumns;

    return (
        <Box sx={{ height: '100%', width: '100%' }}>
            <DataGrid
                key={`customer-grid-${customers.length}`}
                getRowId={(row) => row.customerId}
                rows={customers}
                columns={columns}
                loading={loading}
                editMode="row"
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                onRowEditStop={handleRowEditStop}
                processRowUpdate={processRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                onColumnResize={handleColumnResize}
                slots={{
                    toolbar: EditToolbar,
                    loadingOverlay: LinearProgress,
                }}
                slotProps={{
                    toolbar: {
                        customers,
                        setCustomers,
                        setRowModesModel,
                        selectedCustomer,
                        setSnackbar,
                        handleOpenConfirmationDialog
                    }
                }}
                onRowClick={handlePromotionRowClick}
                pageSize={10}
            />
            {!!snackbar && (
                <Snackbar
                    open
                    anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
                    onClose={handleCloseSnackbar}
                    autoHideDuration={6000}
                >
                    <Alert {...snackbar} onClose={handleCloseSnackbar}/>
                </Snackbar>
            )}
            <Dialog
                open={openConfirmationDialog}
                onClose={handleCloseConfirmationDialog}
                PaperComponent={PaperComponent}
                aria-labelledby="draggable-dialog-title"
            >
                <DialogTitle style={{cursor: 'move'}} id="draggable-dialog-title">
                    Confirm Deletion
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this customer? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseConfirmationDialog} color="primary">
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDeleteCustomer} color="primary">
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CustomerTable;
